/**
 * Shared category icon mapping for the web app
 * Maps database category icon enum values to Material-UI icon components
 * 
 * Database stores enum values (e.g., 'shopping', 'creditCard', 'debt')
 * Web app needs Material-UI icon components
 */

import React from 'react';
import {
  Home as HomeIcon,
  DirectionsCar as CarIcon,
  Restaurant as FoodIcon,
  LocalHospital as HealthIcon,
  ShoppingCart as ShoppingCartIcon,
  School as EducationIcon,
  Theaters as EntertainmentIcon,
  Power as UtilitiesIcon,
  Savings as SavingsIcon,
  Category as OtherIcon,
  LocalGroceryStore as GroceryIcon,
  CreditCard as CreditCardIcon,
  Pets as PetsIcon,
  CardGiftcard as GiftsIcon,
  Flight as TravelIcon,
  AccountBalance as InvestmentIcon,
  Policy as InsuranceIcon,
  Payment as DebtIcon,
  LocalLaundryService as LaundryIcon,
  Phone as PhoneIcon,
  ChildCare as ChildCareIcon,
  FitnessCenter as GymIcon,
  LocalParking as ParkingIcon,
  LocalParking as ParkingFeeIcon,
  Commute as CommuteIcon,
  LocalCafe as CoffeeIcon,
  Wifi as InternetIcon,
  Celebration as BirthdayIcon,
  BeachAccess as VacationIcon,
  LocalLibrary as BooksIcon,
  Brush as BeautyIcon,
  Construction as MaintenanceIcon,
  Medication as MedicationIcon,
  SportsEsports as GamingIcon,
  VolunteerActivism as CharityIcon,
  LocalAtm as CashIcon,
  AccountBalanceWallet as WalletIcon,
  Receipt as BillsIcon,
  LocalTaxi as TaxiIcon,
  Train as TransitIcon,
  LocalMall as ShoppingMallIcon,
  Store as RetailIcon,
  Redeem as GiftCardIcon,
  Apartment as RentIcon,
  Business as OfficeIcon,
  LocalHotel as HotelIcon,
  LocalBar as NightlifeIcon,
  SportsBasketball as SportsIcon,
  Spa as WellnessIcon,
  Devices as ElectronicsIcon,
  Palette as HobbyIcon,
  Deck as OutdoorIcon,
  Storefront as BusinessIcon,
  LocalFlorist as GardenIcon,
  Umbrella as EmergencyIcon,
  Lightbulb as ElectricityIcon,
  WaterDrop as WaterIcon,
  LocalShipping as ShippingIcon,
  Newspaper as SubscriptionsIcon,
  Loyalty as RewardsIcon,
  LocalLaundryService as DryCleaningIcon,
  Healing as TherapyIcon,
  Tune as MaintenanceRepairIcon,
  Security as SecurityIcon,
  Dns as HostingIcon,
  Language as InternationalIcon,
  Forum as ConsultingIcon,
  School as TuitionIcon,
  LocalPrintshop as PrintingIcon,
  PhotoCamera as PhotographyIcon,
  MusicNote as MusicIcon,
  Mic as AudioIcon,
  Code as SoftwareIcon,
  Storage as StorageIcon,
  Toll as TollsIcon,
  LocalGasStation as GasIcon,
  Build as ToolsIcon,
  Nature as SustainabilityIcon,
  MonetizationOn as InvestmentsIcon,
  AccountBalanceWallet as CryptoIcon,
  TrendingUp as StocksIcon,
  AttachMoney as LoansIcon,
  AccountBalance as BankFeesIcon
} from '@mui/icons-material';

export const CATEGORY_ICONS = {
  // Housing & Utilities
  home: <HomeIcon />,
  rent: <RentIcon />,
  office: <OfficeIcon />,
  hotel: <HotelIcon />,
  utilities: <UtilitiesIcon />,
  electricity: <ElectricityIcon />,
  water: <WaterIcon />,
  maintenance: <MaintenanceIcon />,
  maintenanceRepair: <MaintenanceRepairIcon />,
  garden: <GardenIcon />,
  tools: <ToolsIcon />,

  // Transportation
  car: <CarIcon />,
  taxi: <TaxiIcon />,
  transit: <TransitIcon />,
  travel: <TravelIcon />,
  commute: <CommuteIcon />,
  parking: <ParkingIcon />,
  parkingFee: <ParkingFeeIcon />,
  tolls: <TollsIcon />,
  gas: <GasIcon />,

  // Food & Dining
  food: <FoodIcon />,
  grocery: <GroceryIcon />,
  coffee: <CoffeeIcon />,
  nightlife: <NightlifeIcon />,

  // Shopping & Retail
  shopping: <ShoppingCartIcon />,
  shoppingMall: <ShoppingMallIcon />,
  retail: <RetailIcon />,
  electronics: <ElectronicsIcon />,
  giftCard: <GiftCardIcon />,
  shipping: <ShippingIcon />,

  // Health & Wellness
  health: <HealthIcon />,
  medication: <MedicationIcon />,
  therapy: <TherapyIcon />,
  gym: <GymIcon />,
  wellness: <WellnessIcon />,
  beauty: <BeautyIcon />,
  drycleaning: <DryCleaningIcon />,
  laundry: <LaundryIcon />,

  // Entertainment & Hobbies
  entertainment: <EntertainmentIcon />,
  gaming: <GamingIcon />,
  sports: <SportsIcon />,
  hobby: <HobbyIcon />,
  outdoor: <OutdoorIcon />,
  books: <BooksIcon />,
  music: <MusicIcon />,
  audio: <AudioIcon />,
  photography: <PhotographyIcon />,

  // Financial
  cash: <CashIcon />,
  wallet: <WalletIcon />,
  bills: <BillsIcon />,
  savings: <SavingsIcon />,
  investment: <InvestmentIcon />,
  investments: <InvestmentsIcon />,
  crypto: <CryptoIcon />,
  stocks: <StocksIcon />,
  loans: <LoansIcon />,
  debt: <DebtIcon />,
  bankFees: <BankFeesIcon />,
  creditCard: <CreditCardIcon />,
  insurance: <InsuranceIcon />,
  emergency: <EmergencyIcon />,

  // Business & Professional
  business: <BusinessIcon />,
  consulting: <ConsultingIcon />,
  software: <SoftwareIcon />,
  hosting: <HostingIcon />,
  storage: <StorageIcon />,
  printing: <PrintingIcon />,
  international: <InternationalIcon />,
  security: <SecurityIcon />,

  // Education
  education: <EducationIcon />,
  tuition: <TuitionIcon />,

  // Subscriptions & Services
  internet: <InternetIcon />,
  phone: <PhoneIcon />,
  subscriptions: <SubscriptionsIcon />,
  rewards: <RewardsIcon />,

  // Family & Personal
  childcare: <ChildCareIcon />,
  pets: <PetsIcon />,
  gifts: <GiftsIcon />,
  birthday: <BirthdayIcon />,
  vacation: <VacationIcon />,

  // Miscellaneous
  charity: <CharityIcon />,
  sustainability: <SustainabilityIcon />,
  other: <OtherIcon />
};

/**
 * MUI SvgIcon.fontSize is an enum (`inherit` | `small` | `medium` | `large`).
 * A numeric pixel size must live on `sx.fontSize` or capitalize() throws and
 * the whole tree unmounts.
 */
export const sanitizeMuiIconProps = (props = {}) => {
  const { fontSize, sx, ...rest } = props;
  if (typeof fontSize !== 'number') {
    return props;
  }
  const nextSx = sx && typeof sx === 'object' ? { fontSize, ...sx } : { fontSize };
  return { ...rest, sx: nextSx };
};

/**
 * Get the icon component for a category icon enum value
 * @param {string} iconName - The icon enum value from the database
 * @param {object} props - Optional props to pass to the icon component
 * @returns {React.Element} The icon component, or OtherIcon as fallback
 */
export const getCategoryIcon = (iconName, props = {}) => {
  const IconComponent = CATEGORY_ICONS[iconName] || CATEGORY_ICONS.other;
  const nextProps = sanitizeMuiIconProps(props);
  return IconComponent ? React.cloneElement(IconComponent, nextProps) : <OtherIcon {...nextProps} />;
};

